import ApiService from '@services/api.service'
import { Notify } from 'quasar'

export const bilanParcours = {
  namespaced: true,
  state: {
    //Listes et collections.
    students: [],
    student_assessments: {},
    selected_student: null,
    selected_assessment: null,

    //Données de référence.
    diplomes: [],
    entreprises: [],
    contacts: [],
    contrats: {},
    evaluations: {},
    student_interviews: {},
    student_evaluations_centre: {},

    //États UI.
    loading_students: false,
    loading_assessments: false,
    loading_references: false
  },

  mutations: {
    SET_STUDENTS(state, students) {
      state.students = students
    },
    SET_STUDENT_ASSESSMENTS(state, { student_id, assessments }) {
      if (!state.student_assessments[student_id]) {
        state.student_assessments[student_id] = []
      }
      state.student_assessments[student_id] = assessments
    },
    SET_SELECTED_STUDENT(state, student) {
      state.selected_student = student
    },
    SET_SELECTED_ASSESSMENT(state, assessment) {
      state.selected_assessment = assessment
    },
    UPDATE_ASSESSMENT(state, assessment) {
      if (!state.selected_student) return

      const student_id = state.selected_student.id
      if (!state.student_assessments[student_id]) return

      const index = state.student_assessments[student_id].findIndex(
        (a) => a.id === assessment.id
      )

      if (index !== -1) {
        state.student_assessments[student_id][index] = assessment
      } else {
        state.student_assessments[student_id].push(assessment)
      }
    },
    SET_DIPLOMES(state, diplomes) {
      state.diplomes = diplomes
    },
    SET_ENTREPRISES(state, entreprises) {
      state.entreprises = entreprises
    },
    SET_CONTACTS(state, contacts) {
      state.contacts = contacts
    },
    SET_STUDENT_CONTRATS(state, { student_id, contrats }) {
      state.contrats[student_id] = contrats
    },
    SET_STUDENT_EVALUATIONS(state, { student_id, evaluations }) {
      state.evaluations[student_id] = evaluations
    },
    SET_STUDENT_EVALUATIONS_CENTRE(state, { student_id, evaluations }) {
      state.student_evaluations_centre[student_id] = evaluations
    },
    SET_LOADING_STUDENTS(state, status) {
      state.loading_students = status
    },
    SET_LOADING_ASSESSMENTS(state, status) {
      state.loading_assessments = status
    },
    SET_LOADING_REFERENCES(state, status) {
      state.loading_references = status
    },
    SET_STUDENT_INTERVIEWS(state, { student_id, interviews }) {
      state.student_interviews[student_id] = interviews
    }
  },

  actions: {
    /**
     * Charge la liste des étudiants pour les bilans de parcours.
     */
    async fetchStudents({ commit }) {
      commit('SET_LOADING_STUDENTS', true)
      try {
        const response = await ApiService.getRequest(
          'stagiaires_bilan_parcours?etat=0&type=1'
        )
        commit('SET_STUDENTS', response.data['hydra:member'] || [])
        return response.data['hydra:member'] || []
      } catch (error) {
        console.error('Erreur lors du chargement des étudiants:', error)
        Notify.create({
          message: 'Erreur lors du chargement des étudiants',
          color: 'negative'
        })
        return []
      } finally {
        commit('SET_LOADING_STUDENTS', false)
      }
    },

    /**
     * Charge les détails d'un étudiant, incluant ses bilans.
     */
    async fetchStudentDetails({ commit, state }, student_id) {
      commit('SET_LOADING_ASSESSMENTS', true)
      try {
        const response = await ApiService.getRequest(
          `stagiaires_bilan_parcours/${student_id}`
        )
        const student = response.data

        //Stocker l'étudiant sélectionné.
        commit('SET_SELECTED_STUDENT', student)

        //Extraire et stocker les bilans.
        const assessments =
          student.etudiant_bilan_parcours || student.etudiantBilanParcours || []
        commit('SET_STUDENT_ASSESSMENTS', {
          student_id: student.id,
          assessments
        })

        return student
      } catch (error) {
        console.error(
          "Erreur lors du chargement des détails de l'étudiant:",
          error
        )
        Notify.create({
          message: "Erreur lors du chargement des détails de l'étudiant",
          color: 'negative'
        })
        return null
      } finally {
        commit('SET_LOADING_ASSESSMENTS', false)
      }
    },

    /**
     * Charge les données de référence (diplômes, entreprises, contacts).
     */
    async fetchReferenceData({ commit }) {
      commit('SET_LOADING_REFERENCES', true)
      try {
        const [entreprisesResponse, contactsResponse] = await Promise.all([
          ApiService.getRequest('entreprises_light_list'),
          ApiService.getRequest('entreprise_contacts_light_list')
        ])

        commit(
          'SET_ENTREPRISES',
          entreprisesResponse.data['hydra:member'] || []
        )
        commit('SET_CONTACTS', contactsResponse.data['hydra:member'] || [])

        return {
          entreprises: entreprisesResponse.data['hydra:member'] || [],
          contacts: contactsResponse.data['hydra:member'] || []
        }
      } catch (error) {
        console.error(
          'Erreur lors du chargement des données de référence:',
          error
        )
        Notify.create({
          message: 'Erreur lors du chargement des données de référence',
          color: 'negative'
        })
        return { entreprises: [], contacts: [] }
      } finally {
        commit('SET_LOADING_REFERENCES', false)
      }
    },

    /**
     * Charge les contrats d'un étudiant.
     */
    async fetchStudentContracts({ commit }, student_id) {
      try {
        //Récupérer la liste des contrats de l'étudiant.
        const response = await ApiService.getRequest(
          `etudiants/${student_id}/contrats`
        )
        const contrats = response.data['hydra:member'] || []

        commit('SET_STUDENT_CONTRATS', { student_id, contrats })
        return contrats
      } catch (error) {
        console.error('Erreur lors du chargement des contrats:', error)
        Notify.create({
          message: "Erreur lors du chargement des contrats de l'étudiant",
          color: 'negative'
        })
        return []
      }
    },

    /**
     * Charge les évaluations d'un étudiant.
     */
    async fetchStudentEvaluations({ commit }, student_id) {
      try {
        const response = await ApiService.getRequest(
          `etudiants/${student_id}/evaluations`
        )
        const evaluations = response.data['hydra:member'] || []

        commit('SET_STUDENT_EVALUATIONS', { student_id, evaluations })
        return evaluations
      } catch (error) {
        console.error('Erreur lors du chargement des évaluations:', error)
        Notify.create({
          message: "Erreur lors du chargement des évaluations de l'étudiant",
          color: 'negative'
        })
        return []
      }
    },

    /**
     * Charge les entretiens d'un étudiant.
     */
    async fetchStudentInterviews({ commit }, student_id) {
      try {
        const response = await ApiService.getRequest(
          `stagiaires_liste_entretiens/${student_id}`
        )

        // Extraction des entretiens de la réponse
        const interviews =
          response.data && response.data.suivis ? response.data.suivis : []

        // Stocker dans le state
        commit('SET_STUDENT_INTERVIEWS', { student_id, interviews })

        return interviews
      } catch (error) {
        console.error('Erreur lors du chargement des entretiens:', error)
        Notify.create({
          message: "Erreur lors du chargement des entretiens de l'étudiant",
          color: 'negative'
        })
        return []
      }
    },

    /**
     * Charge les évaluations centre d'un étudiant.
     */
    async fetchStudentEvaluationsCentre({ commit }, student_id) {
      try {
        const response = await ApiService.getRequest(
          `stagiaires_evaluations/${student_id}`
        )

        const evaluations = response.data.antenne_evaluations || []

        commit('SET_STUDENT_EVALUATIONS_CENTRE', { student_id, evaluations })
        return evaluations
      } catch (error) {
        console.error(
          'Erreur lors du chargement des évaluations de centre:',
          error
        )
        Notify.create({
          message: "Erreur lors du chargement des évaluations de l'étudiant",
          color: 'negative'
        })
        return []
      }
    },

    /**
     * Crée un nouveau bilan de parcours.
     */
    async createAssessment({ commit }, assessment_data) {
      try {
        const response = await ApiService.postRequest(
          'etudiant_bilan_parcours',
          assessment_data
        )

        Notify.create({
          message: 'Bilan créé avec succès',
          color: 'positive'
        })

        commit('UPDATE_ASSESSMENT', response.data)
        return response.data
      } catch (error) {
        console.error('Erreur lors de la création du bilan:', error)
        const errorMessage =
          error.response?.data?.['hydra:description'] ||
          'Erreur lors de la création du bilan'

        Notify.create({
          message: errorMessage,
          color: 'negative'
        })

        throw error
      }
    },

    /**
     * Met à jour un bilan existant.
     */
    async updateAssessment(
      { commit, dispatch },
      { assessment_id, assessment_data }
    ) {
      try {
        const response = await ApiService.putRequest(
          `etudiant_bilan_parcours/${assessment_id}`,
          assessment_data
        )

        //Récupérer les données fraîches après la mise à jour.
        const refreshResponse = await ApiService.getRequest(
          `etudiant_bilan_parcours/${assessment_id}`
        )

        Notify.create({
          message: 'Bilan mis à jour avec succès',
          color: 'positive'
        })

        commit('UPDATE_ASSESSMENT', refreshResponse.data)

        //Actualiser les suivis RPP pour s'assurer qu'ils sont à jour
        try {
          await dispatch('suiviRpp/fetchSuiviRpps', assessment_id, {
            root: true
          })
        } catch (suiviError) {
          console.warn(
            'Erreur lors de la synchronisation des suivis RPP:',
            suiviError
          )
        }

        return refreshResponse.data
      } catch (error) {
        console.error('Erreur lors de la mise à jour du bilan:', error)
        const errorMessage =
          error.response?.data?.['hydra:description'] ||
          'Erreur lors de la mise à jour du bilan'

        Notify.create({
          message: errorMessage,
          color: 'negative'
        })

        throw error
      }
    },

    /**
     * Archive un bilan en mettant is_archived à true.
     */
    async archiveAssessment({ commit, state }, assessment_id) {
      try {
        const assessment = state.student_assessments[
          state.selected_student.id
        ].find((a) => a.id === assessment_id)

        if (!assessment) {
          throw new Error('Bilan non trouvé')
        }

        const updateData = {
          ...assessment,
          is_archived: true
        }

        const response = await ApiService.putRequest(
          `etudiant_bilan_parcours/${assessment_id}`,
          updateData
        )

        Notify.create({
          message: 'Bilan archivé avec succès',
          color: 'positive'
        })

        commit('UPDATE_ASSESSMENT', response.data)
        return response.data
      } catch (error) {
        console.error("Erreur lors de l'archivage du bilan:", error)

        Notify.create({
          message: "Erreur lors de l'archivage du bilan",
          color: 'negative'
        })

        throw error
      }
    },

    /**
     * Restaure un bilan archivé en mettant is_archived à false.
     */
    async restoreAssessment({ commit, state }, assessment_id) {
      try {
        const assessment = state.student_assessments[
          state.selected_student.id
        ].find((a) => a.id === assessment_id)

        if (!assessment) {
          throw new Error('Bilan non trouvé')
        }

        const updateData = {
          ...assessment,
          is_archived: false
        }

        const response = await ApiService.putRequest(
          `etudiant_bilan_parcours/${assessment_id}`,
          updateData
        )

        Notify.create({
          message: 'Bilan restauré avec succès',
          color: 'positive'
        })

        commit('UPDATE_ASSESSMENT', response.data)
        return response.data
      } catch (error) {
        console.error('Erreur lors de la restauration du bilan:', error)

        Notify.create({
          message: 'Erreur lors de la restauration du bilan',
          color: 'negative'
        })

        throw error
      }
    },

    /**
     * Effectue un soft delete d'un bilan en mettant is_deleted à true.
     */
    async deleteAssessment({ commit, state }, assessment_id) {
      try {
        const assessment = state.student_assessments[
          state.selected_student.id
        ].find((a) => a.id === assessment_id)

        if (!assessment) {
          throw new Error('Bilan non trouvé')
        }

        const updateData = {
          is_deleted: true
        }

        const response = await ApiService.putRequest(
          `etudiant_bilan_parcours/${assessment_id}`,
          updateData
        )

        Notify.create({
          message: 'Bilan supprimé avec succès',
          color: 'positive'
        })

        commit('UPDATE_ASSESSMENT', response.data)
        return response.data
      } catch (error) {
        console.error('Erreur lors de la suppression du bilan:', error)

        Notify.create({
          message: 'Erreur lors de la suppression du bilan',
          color: 'negative'
        })

        throw error
      }
    },

    /**
     *
     * Supprimer une évaluations dans la liste.
     */
    async deleteRequest({ commit }, endpoint) {
      try {
        await ApiService.deleteRequest(endpoint)
        return true
      } catch (error) {
        console.error('Error during API delete request:', error)
        throw error
      }
    },

    /**
     * Met à jour un bilan dans le store local (sans appel API).
     */
    updateAssessmentInStore({ commit }, { assessment_data }) {
      try {
        commit('UPDATE_ASSESSMENT', assessment_data)
      } catch (error) {
        console.error(
          'Erreur lors de la mise à jour du bilan dans le store:',
          error
        )
      }
    }
  },

  getters: {
    getStudents: (state) => state.students,
    getSelectedStudent: (state) => state.selected_student,
    getStudentAssessments: (state) => (student_id) => {
      return state.student_assessments[student_id] || []
    },
    getActiveAssessments: (state) => (student_id) => {
      const assessments = state.student_assessments[student_id] || []
      return assessments.filter((a) => !a.is_archived && !a.is_deleted)
    },
    getArchivedAssessments: (state) => (student_id) => {
      const assessments = state.student_assessments[student_id] || []
      return assessments.filter((a) => a.is_archived && !a.is_deleted)
    },
    getSelectedAssessment: (state) => state.selected_assessment,
    getDiplomes: (state) => state.diplomes,
    getEntreprises: (state) => state.entreprises,
    getContacts: (state) => state.contacts,
    getStudentContracts: (state) => (student_id) => {
      return state.contrats[student_id] || []
    },
    getStudentInterviews: (state) => (student_id) => {
      return state.student_interviews[student_id] || []
    },
    getStudentEvaluations: (state) => (student_id) => {
      return state.evaluations[student_id] || []
    },
    getStudentEvaluationsCentre: (state) => (student_id) => {
      return state.student_evaluations_centre[student_id] || []
    },
    isLoadingStudents: (state) => state.loading_students,
    isLoadingAssessments: (state) => state.loading_assessments,
    isLoadingReferences: (state) => state.loading_references
  }
}

export default bilanParcours
